import Axios from 'axios';
import { apolloClient } from '@/apollo';
import {
  contentLinkBlockPreviewQuery,
  contentLinkBlockUploadQuery,
} from '@/graphql/queries/dealroom.queries';
import { IContentLinkBlock } from '@/dealroom/content-link/types/content-link';
import {
  addDocumentPageMutation,
  createContentLinkBlockMutation,
  updateContentLinkBlockMutation,
} from '@/graphql/mutations/dealroom.mutations';
import { IDocumentPage } from '@/types/document-page';

export class ContentLinkBlockService {
  static async loadContentLinkBlockPreview(targetUrl: string): Promise<Partial<IContentLinkBlock>> {
    const {
      data: { previewContentLinkBlock },
    } = await apolloClient.query({
      query: contentLinkBlockPreviewQuery,
      variables: { targetUrl },
      fetchPolicy: 'network-only',
    });
    return previewContentLinkBlock;
  }

  static async createContentLinkBlock(
    contentLinkBlock: IContentLinkBlock
  ): Promise<IContentLinkBlock> {
    const {
      data: { createContentLinkBlock },
    } = await apolloClient.mutate({
      mutation: createContentLinkBlockMutation,
      variables: { ...contentLinkBlock },
    });
    return createContentLinkBlock;
  }

  static async updateContentLinkBlock(
    contentLinkBlock: IContentLinkBlock
  ): Promise<IContentLinkBlock> {
    const {
      data: { updateContentLinkBlock },
    } = await apolloClient.mutate({
      mutation: updateContentLinkBlockMutation,
      variables: { ...contentLinkBlock },
    });
    return updateContentLinkBlock;
  }

  static async createContentLinkDocumentPage(
    blockId: string,
    pageNum: number,
    documentId: string
  ): Promise<IDocumentPage> {
    const {
      data: { addDocumentPage },
    } = await apolloClient.mutate({
      mutation: addDocumentPageMutation,
      variables: { documentId, blockId, pageNum, pageType: 'contentLinkBlock' },
    });
    return addDocumentPage;
  }

  static async getContentLinkUploadUrl(blockId: string): Promise<string> {
    const { data } = await apolloClient.query({
      query: contentLinkBlockUploadQuery,
      variables: { blockId },
    });
    return data.contentLinkBlockUpload;
  }

  static async uploadThumbnail({
    file,
    uploadLink,
  }: {
    file: File;
    uploadLink: string;
  }): Promise<void> {
    await Axios.put(uploadLink, file);
  }

  static getPayload(
    contentLinkBlock: IContentLinkBlock,
    file: File,
    overwriteThumbUrl: boolean = true
  ): IContentLinkBlock {
    if (file) {
      const [thumbExtension]: string[] = file.name.split('.').reverse();
      return {
        ...contentLinkBlock,
        thumbUrl: undefined,
        thumbExtension,
      };
    } else if (!overwriteThumbUrl) {
      return {
        ...contentLinkBlock,
        thumbUrl: undefined,
      };
    }
    return contentLinkBlock;
  }
}
